import { useEffect, useState } from "react";
import { toast } from "sonner";
import { Plus, Loader2, MoreVertical, Pencil, KeyRound, Trash2 } from "lucide-react";
import PageHeader from "../components/PageHeader";
import { StatusBadge } from "../components/StatusBadge";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from "../components/ui/dialog";
import {
  DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger,
} from "../components/ui/dropdown-menu";
import {
  AlertDialog, AlertDialogContent, AlertDialogHeader, AlertDialogFooter,
  AlertDialogTitle, AlertDialogDescription, AlertDialogCancel, AlertDialogAction,
} from "../components/ui/alert-dialog";
import api, { apiError } from "../lib/api";
import { initials } from "../lib/utils-format";
import { useAuth } from "../context/AuthContext";

const FALLBACK_ROLES = ["Owner", "Administrator", "Marketing Manager", "Sales Manager", "Sales Representative", "Content Creator", "Customer Success Manager", "Analyst", "Read Only"];

export default function Team() {
  const { isAdmin, user: me } = useAuth();
  const [users, setUsers] = useState([]);
  const [roleNames, setRoleNames] = useState(FALLBACK_ROLES);
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState({ firstName: "", lastName: "", email: "", password: "", role: "Sales Representative" });
  const [saving, setSaving] = useState(false);
  const [editUser, setEditUser] = useState(null);
  const [resetUser, setResetUser] = useState(null);
  const [removeUser, setRemoveUser] = useState(null);
  const [workload, setWorkload] = useState({});

  const load = () => api.get("/auth/users").then((r) => setUsers(r.data)).catch(() => {});
  useEffect(() => {
    load();
    api.get("/rep-workload").then((r) => {
      const map = {};
      (r.data.reps || []).forEach((x) => { map[x.id] = x.open_leads; });
      setWorkload(map);
    }).catch(() => {});
    if (isAdmin) api.get("/roles").then((r) => setRoleNames(r.data.items.map((x) => x.role))).catch(() => {});
  }, [isAdmin]);

  const invite = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      const name = `${form.firstName.trim()} ${form.lastName.trim()}`.trim();
      await api.post("/auth/register", { name, email: form.email, password: form.password, role: form.role });
      toast.success("Team member added");
      setOpen(false); setForm({ firstName: "", lastName: "", email: "", password: "", role: "Sales Representative" }); load();
    } catch (err) { toast.error(apiError(err)); } finally { setSaving(false); }
  };

  const changeRole = async (userId, role) => {
    try { await api.put(`/users/${userId}/role`, { role }); toast.success("Role updated"); load(); }
    catch (e) { toast.error(apiError(e)); }
  };

  const doRemove = async () => {
    const u = removeUser;
    setRemoveUser(null);
    try { await api.delete(`/users/${u.id}`); toast.success("Member removed"); load(); }
    catch (e) { toast.error(apiError(e)); }
  };

  const ROLES = roleNames;

  return (
    <div className="p-6 lg:p-8 max-w-[1200px] mx-auto">
      <PageHeader title="Team" count={users.length} subtitle="Users, roles, and permissions"
        actions={<Button onClick={() => setOpen(true)} data-testid="new-record-btn"><Plus className="h-4 w-4 mr-1.5" />Add Member</Button>} />

      <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4">
        {users.map((u, idx) => (
          <div key={u.id} className="rounded-md border border-border bg-card p-5 fade-up" style={{ animationDelay: `${idx * 30}ms` }} data-testid={`user-card-${u.id}`}>
            <div className="flex items-center gap-4">
              <div className="h-12 w-12 rounded-full bg-primary text-primary-foreground flex items-center justify-center font-semibold">{initials(u.name)}</div>
              <div className="min-w-0 flex-1">
                <div className="font-medium truncate">{u.name}</div>
                <div className="text-xs text-muted-foreground truncate">{u.email}</div>
                <div className="mt-1.5"><StatusBadge value={u.role} /></div>
                {u.role === "Sales Representative" && (
                  <div className="mt-1.5 text-xs text-muted-foreground" data-testid={`user-workload-${u.id}`}>
                    <span className="font-semibold text-foreground">{workload[u.id] ?? 0}</span> open lead{(workload[u.id] ?? 0) === 1 ? "" : "s"}
                  </div>
                )}
              </div>
              {isAdmin && (
                <DropdownMenu>
                  <DropdownMenuTrigger asChild>
                    <button className="p-1.5 -mr-1 rounded-md text-muted-foreground hover:text-foreground hover:bg-accent transition-colors self-start" data-testid={`user-actions-${u.id}`}>
                      <MoreVertical className="h-4 w-4" />
                    </button>
                  </DropdownMenuTrigger>
                  <DropdownMenuContent align="end" className="w-44">
                    <DropdownMenuItem onClick={() => setEditUser({ id: u.id, name: u.name, email: u.email })} data-testid={`user-edit-${u.id}`}>
                      <Pencil className="h-4 w-4 mr-2" />Edit details
                    </DropdownMenuItem>
                    <DropdownMenuItem onClick={() => setResetUser({ id: u.id, name: u.name, password: "" })} data-testid={`user-reset-${u.id}`}>
                      <KeyRound className="h-4 w-4 mr-2" />Reset password
                    </DropdownMenuItem>
                    {u.id !== me?.id && (
                      <DropdownMenuItem onClick={() => setRemoveUser(u)} className="text-destructive focus:text-destructive" data-testid={`user-remove-${u.id}`}>
                        <Trash2 className="h-4 w-4 mr-2" />Remove member
                      </DropdownMenuItem>
                    )}
                  </DropdownMenuContent>
                </DropdownMenu>
              )}
            </div>
            {isAdmin && (
              <div className="mt-4 pt-3 border-t border-border">
                <Label className="text-[10px] uppercase tracking-wide text-muted-foreground">Assign Role</Label>
                <Select value={u.role} onValueChange={(v) => changeRole(u.id, v)}>
                  <SelectTrigger className="mt-1 h-8 text-sm" data-testid={`user-role-select-${u.id}`}><SelectValue /></SelectTrigger>
                  <SelectContent>{ROLES.map((r) => <SelectItem key={r} value={r}>{r}</SelectItem>)}</SelectContent>
                </Select>
              </div>
            )}
          </div>
        ))}
      </div>

      {/* Add member */}
      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle className="font-display tracking-tight">Add Team Member</DialogTitle>
            <DialogDescription>They'll sign in with this email and password. Their role controls what they can see.</DialogDescription>
          </DialogHeader>
          <form onSubmit={invite} className="space-y-4">
            <div className="grid grid-cols-2 gap-3">
              <div className="space-y-1.5"><Label>First name</Label><Input value={form.firstName} onChange={(e) => setForm({ ...form, firstName: e.target.value })} required data-testid="member-first-name" /></div>
              <div className="space-y-1.5"><Label>Last name</Label><Input value={form.lastName} onChange={(e) => setForm({ ...form, lastName: e.target.value })} required data-testid="member-last-name" /></div>
            </div>
            <div className="space-y-1.5"><Label>Email</Label><Input type="email" value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} required data-testid="member-email" /></div>
            <div className="space-y-1.5"><Label>Temporary Password</Label><Input type="text" value={form.password} onChange={(e) => setForm({ ...form, password: e.target.value })} required minLength={6} data-testid="member-password" /></div>
            <div className="space-y-1.5"><Label>Role</Label>
              <Select value={form.role} onValueChange={(v) => setForm({ ...form, role: v })}>
                <SelectTrigger data-testid="member-role"><SelectValue /></SelectTrigger>
                <SelectContent>{ROLES.map((r) => <SelectItem key={r} value={r}>{r}</SelectItem>)}</SelectContent>
              </Select>
            </div>
            <DialogFooter>
              <Button type="button" variant="outline" onClick={() => setOpen(false)}>Cancel</Button>
              <Button type="submit" disabled={saving} data-testid="member-submit">{saving && <Loader2 className="h-4 w-4 mr-2 animate-spin" />}Add</Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>

      {/* Edit details */}
      <EditMemberDialog member={editUser} onClose={() => setEditUser(null)} onSaved={() => { setEditUser(null); load(); }} />

      {/* Reset password */}
      <ResetPasswordDialog member={resetUser} onClose={() => setResetUser(null)} onSaved={() => setResetUser(null)} />

      {/* Remove member */}
      <AlertDialog open={!!removeUser} onOpenChange={(o) => !o && setRemoveUser(null)}>
        <AlertDialogContent data-testid="confirm-remove-member-dialog">
          <AlertDialogHeader>
            <AlertDialogTitle>Remove “{removeUser?.name}”?</AlertDialogTitle>
            <AlertDialogDescription>They will lose access immediately and won't be able to sign in. This action cannot be undone.</AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel data-testid="confirm-remove-member-cancel">Cancel</AlertDialogCancel>
            <AlertDialogAction onClick={doRemove} className="bg-destructive text-destructive-foreground hover:bg-destructive/90" data-testid="confirm-remove-member-confirm">Remove</AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}

function EditMemberDialog({ member, onClose, onSaved }) {
  const [firstName, setFirstName] = useState("");
  const [lastName, setLastName] = useState("");
  const [email, setEmail] = useState("");
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (member) {
      const parts = (member.name || "").trim().split(/\s+/);
      setFirstName(parts[0] || "");
      setLastName(parts.slice(1).join(" "));
      setEmail(member.email || "");
    }
  }, [member]);

  const save = async () => {
    setSaving(true);
    try {
      const name = `${firstName.trim()} ${lastName.trim()}`.trim();
      await api.put(`/users/${member.id}`, { name, email });
      toast.success("Member updated");
      onSaved();
    } catch (e) { toast.error(apiError(e)); } finally { setSaving(false); }
  };

  return (
    <Dialog open={!!member} onOpenChange={(o) => !o && onClose()}>
      <DialogContent data-testid="edit-member-dialog">
        <DialogHeader>
          <DialogTitle className="font-display tracking-tight">Edit Member</DialogTitle>
          <DialogDescription>Update this member's name or email address.</DialogDescription>
        </DialogHeader>
        <div className="space-y-4 py-1">
          <div className="grid grid-cols-2 gap-3">
            <div className="space-y-1.5"><Label>First name</Label><Input value={firstName} onChange={(e) => setFirstName(e.target.value)} data-testid="edit-member-first-name" /></div>
            <div className="space-y-1.5"><Label>Last name</Label><Input value={lastName} onChange={(e) => setLastName(e.target.value)} data-testid="edit-member-last-name" /></div>
          </div>
          <div className="space-y-1.5"><Label>Email</Label><Input type="email" value={email} onChange={(e) => setEmail(e.target.value)} data-testid="edit-member-email" /></div>
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={onClose}>Cancel</Button>
          <Button onClick={save} disabled={saving || !firstName.trim() || !email.trim()} data-testid="edit-member-save">{saving && <Loader2 className="h-4 w-4 mr-2 animate-spin" />}Save</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function ResetPasswordDialog({ member, onClose, onSaved }) {
  const [password, setPassword] = useState("");
  const [saving, setSaving] = useState(false);

  useEffect(() => { if (member) setPassword(""); }, [member]);

  const save = async () => {
    setSaving(true);
    try {
      await api.post(`/users/${member.id}/reset-password`, { password });
      toast.success(`Password reset for ${member.name}`);
      onSaved();
    } catch (e) { toast.error(apiError(e)); } finally { setSaving(false); }
  };

  return (
    <Dialog open={!!member} onOpenChange={(o) => !o && onClose()}>
      <DialogContent data-testid="reset-password-dialog">
        <DialogHeader>
          <DialogTitle className="font-display tracking-tight">Reset Password</DialogTitle>
          <DialogDescription>Set a new password for {member?.name}. Share it with them securely; they can change it later.</DialogDescription>
        </DialogHeader>
        <div className="space-y-1.5 py-1">
          <Label>New password</Label>
          <Input type="text" value={password} onChange={(e) => setPassword(e.target.value)} placeholder="At least 6 characters" minLength={6} data-testid="reset-password-input" />
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={onClose}>Cancel</Button>
          <Button onClick={save} disabled={saving || password.length < 6} data-testid="reset-password-save">{saving && <Loader2 className="h-4 w-4 mr-2 animate-spin" />}Set Password</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
